import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { donationSchemasAPI } from '../../services/api';

// Async thunk to fetch donation schemas
export const fetchDonationSchemas = createAsyncThunk(
  'donationSchemas/fetchDonationSchemas',
  async (_, { rejectWithValue }) => {
    try {
      // Use large limit for dropdown usage (1000 items)
      const response = await donationSchemasAPI.getAll({ limit: 1000 });
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch donation schemas');
    }
  },
);

export const createDonationSchema = createAsyncThunk(
  'donationSchemas/createDonationSchema',
  async (schemaData, { rejectWithValue }) => {
    try {
      const response = await donationSchemasAPI.create(schemaData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create donation schema');
    }
  },
);

export const updateDonationSchema = createAsyncThunk(
  'donationSchemas/updateDonationSchema',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await donationSchemasAPI.update(id, data);
      return response.data || { ...data, id };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update donation schema');
    }
  },
);

export const deleteDonationSchema = createAsyncThunk(
  'donationSchemas/deleteDonationSchema',
  async (id, { rejectWithValue }) => {
    try {
      await donationSchemasAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete donation schema');
    }
  },
);

const donationSchemasSlice = createSlice({
  name: 'donationSchemas',
  initialState: {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    loading: false,
    error: null,
  },
  reducers: {
    clearDonationSchemasError: (state) => {
      state.error = null;
    },
    invalidateDonationSchemas: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch donation schemas
      .addCase(fetchDonationSchemas.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDonationSchemas.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDonationSchemas.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      })
      // Create donation schema
      .addCase(createDonationSchema.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      // Update donation schema
      .addCase(updateDonationSchema.fulfilled, (state, action) => {
        const index = state.items.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // Delete donation schema
      .addCase(deleteDonationSchema.fulfilled, (state, action) => {
        state.items = state.items.filter((s) => s.id !== action.payload);
      });
  },
});

export const { clearDonationSchemasError, invalidateDonationSchemas } =
  donationSchemasSlice.actions;

// Selectors
export const selectDonationSchemas = (state) => state.donationSchemas.items;
export const selectDonationSchemasLoading = (state) => state.donationSchemas.loading;
export const selectDonationSchemasError = (state) => state.donationSchemas.error;
export const selectDonationSchemasStatus = (state) => state.donationSchemas.status;

export default donationSchemasSlice.reducer;
